import React, { useEffect, useState } from "react";
import {
Card,
CardBody,
CardTitle,
Table,
Button,
Spinner,
Alert,
Row,
Col,
Input,
Modal,
ModalHeader,
ModalBody,
} from "reactstrap";

import {
getAllUserLeaves,
deleteLeave,
} from "../../Api/ApiUrlConst";
import { Navigate, useNavigate } from "react-router-dom";

interface AdminLeave {
leaveId: number;
userId: string;
userName: string;
leaveType: string;
fromDate: string;
toDate: string;
days: number;
reason: string;
}

const AdminLeaveHistory: React.FC = () => {

const [leaves, setLeaves] = useState<AdminLeave[]>([]);
const [loading, setLoading] = useState(true);
const [error, setError] = useState("");

const [year, setYear] = useState(new Date().getFullYear());

const [searchUser, setSearchUser] = useState("");
const [searchLeaveType, setSearchLeaveType] = useState("");

const [period, setPeriod] = useState("all");
const [startDate, setStartDate] = useState("");
const [endDate, setEndDate] = useState("");

const [reasonModalOpen, setReasonModalOpen] = useState(false);
const [reasonText, setReasonText] = useState("");

const navigate = useNavigate();

useEffect(() => {
loadLeaves();
}, [year]);

const loadLeaves = async () => {
setLoading(true);
try {
const res = await getAllUserLeaves(year);
setLeaves(res.data);
} catch {
setError("Failed to load leave data.");
} finally {
setLoading(false);
}
};

const handleDelete = async (leaveId: number) => {
if (!window.confirm("Delete this leave record?")) return;
await deleteLeave(leaveId);
loadLeaves();
};

const formatDate = (date: string) =>
new Date(date).toLocaleDateString("en-GB", {
day: "numeric",
month: "short",
year: "numeric",
weekday: "short",
});

/* ---------------- Dropdown Data ---------------- */

const userList = Array.from(new Set(leaves.map(l => l.userName)));
const leaveTypeList = Array.from(new Set(leaves.map(l => l.leaveType)));

/* ---------------- Period Range ---------------- */

const getPeriodRange = () => {


if (period === "all") return null;

const today = new Date();
today.setHours(0, 0, 0, 0);

let start = new Date(today);
let end = new Date(today);

switch (period) {

  case "today":
    break;

  case "thisWeek": {
    const day = today.getDay();
    start.setDate(today.getDate() - day);
    end = new Date(start);
    end.setDate(start.getDate() + 6);
    break;
  }

  case "lastWeek": {
    const day = today.getDay();
    end.setDate(today.getDate() - day - 1);
    start = new Date(end);
    start.setDate(end.getDate() - 6);
    break;
  }

  case "thisMonth":
    start = new Date(today.getFullYear(), today.getMonth(), 1);
    end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    break;

  case "lastMonth":
    start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    end = new Date(today.getFullYear(), today.getMonth(), 0);
    break;

  case "custom":
    if (!startDate || !endDate) return null;
    start = new Date(startDate);
    end = new Date(endDate);
    break;
}

start.setHours(0, 0, 0, 0);
end.setHours(23, 59, 59, 999);

return { start, end };


};

const range = getPeriodRange();

/* ---------------- Filter Logic ---------------- */

const filteredLeaves = leaves.filter(l => {


const matchUser =
  !searchUser || l.userName === searchUser;

const matchType =
  !searchLeaveType || l.leaveType === searchLeaveType;

if (!range) return matchUser && matchType;

const from = new Date(l.fromDate);
const to = new Date(l.toDate);

const matchDate = from <= range.end && to >= range.start;

return matchUser && matchType && matchDate;


});

/* ---------------- Clear Filters ---------------- */

const clearFilters = () => {
setSearchUser("");
setSearchLeaveType("");
setPeriod("all");
setStartDate("");
setEndDate("");
};

if (loading) {
return ( <div className="text-center mt-5"> <Spinner color="primary" /> </div>
);
}

return ( <div className="mt-4">


  <Card className="shadow-sm">
    <CardBody>
<Row>
  <Button
          color="primary"
          onClick={() => navigate("/admin/leave-manage")}
        >
          Manage
        </Button>
</Row>
      <Row className="mb-3 mt-3">

        <Col md="2">
          <CardTitle tag="h4">
            Leave History
          </CardTitle>
        </Col>

        {/* Period */}

        <Col md="1">
          <Input
            type="select"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
          >
            <option value="all">All</option>
            <option value="today">Today</option>
            <option value="thisWeek">This Week</option>
            <option value="lastWeek">Last Week</option>
            <option value="thisMonth">This Month</option>
            <option value="lastMonth">Last Month</option>
          </Input>
        </Col>

        {/* Custom Dates */}

        <Col md="2">
          <Input
            type="date"
            value={startDate}
            onChange={(e) => {
              setStartDate(e.target.value);
              setPeriod("custom");
            }}
          />
        </Col>

        <Col md="2">
          <Input
            type="date"
            value={endDate}
            onChange={(e) => {
              setEndDate(e.target.value);
              setPeriod("custom");
            }}
          />
        </Col>

        {/* Employee Dropdown */}

        <Col md="2">
          <Input
            type="select"
            value={searchUser}
            onChange={(e) => setSearchUser(e.target.value)}
          >
            <option value="">All Employees</option>

            {userList.map((user, index) => (
              <option key={index} value={user}>
                {user}
              </option>
            ))}
          </Input>
        </Col>

        {/* Leave Type Dropdown */}

        <Col md="2">
          <Input
            type="select"
            value={searchLeaveType}
            onChange={(e) => setSearchLeaveType(e.target.value)}
          >
            <option value="">All Leave Types</option>

            {leaveTypeList.map((type, index) => (
              <option key={index} value={type}>
                {type}
              </option>
            ))}
          </Input>
        </Col>

        {/* Year */}

        <Col md="1">
          <Input
            type="number"
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
          />
        </Col>

        {/* Clear Button */}

        <Col md="1">
          <Button
            color="secondary"
            outline
            onClick={clearFilters}
          >
            Clear
          </Button>
        </Col>

      </Row>

      {error && <Alert color="danger">{error}</Alert>}

      <Table bordered responsive hover>

        <thead>
          <tr>
            <th>#</th>
            <th>Employee</th>
            <th>Leave Type</th>
            <th>From</th>
            <th>To</th>
            <th>Days</th>
            <th>Reason</th>
            <th>Action</th>
          </tr>
        </thead>

        <tbody>

          {filteredLeaves.length === 0 && (
            <tr>
              <td colSpan={8} className="text-center">
                No leave records found.
              </td>
            </tr>
          )}

          {filteredLeaves.map((leave, index) => (
            <tr key={leave.leaveId}>

              <td>{index + 1}</td>
              <td>{leave.userName}</td>
              <td>{leave.leaveType}</td>
              <td>{formatDate(leave.fromDate)}</td>
              <td>{formatDate(leave.toDate)}</td>
              <td>{leave.days}</td>

              <td>

                {leave.reason &&
                leave.reason.trim().split(/\s+/).length > 2 ? (

                  <>
                    {leave.reason
                      .split(/\s+/)
                      .slice(0, 2)
                      .join(" ")}
                    ...

                    <Button
                      size="sm"
                      color="link"
                      onClick={() => {
                        setReasonText(leave.reason);
                        setReasonModalOpen(true);
                      }}
                    >
                      More...
                    </Button>
                  </>

                ) : (
                  leave.reason
                )}

              </td>

              <td>

                <Button
                  size="sm"
                  color="danger"
                  onClick={() =>
                    handleDelete(leave.leaveId)
                  }
                >
                  Delete
                </Button>

              </td>

            </tr>
          ))}

        </tbody>

      </Table>

    </CardBody>
  </Card>

  <Modal
    isOpen={reasonModalOpen}
    toggle={() => setReasonModalOpen(false)}
  >

    <ModalHeader toggle={() => setReasonModalOpen(false)}>
      Leave Reason
    </ModalHeader>

    <ModalBody>
      <Input
        type="textarea"
        rows={6}
        value={reasonText}
        readOnly
      />
    </ModalBody>

  </Modal>

</div>

);
};

export default AdminLeaveHistory;
